import {
  Button,
  Grid,
  GridColumn,
  Heading,
  Icon,
  List,
  ListItem,
  Modal,
  ModalContent,
  ModalProvider,
  Notice,
  PassField,
  Separator,
  Text,
} from 'telia-front-react';
import { observer } from 'mobx-react';
import React from 'react';
import useStores from '../stores/UseStores';
import { confirmPwFieldHelper, getHelperErrorMessage, notEmptyFieldHelper, strongPwFieldHelper } from '../util/FieldHelperUtil';
import { useDidUpdate } from '../util/hooks/useDidUpdate';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';
import { confirmPasswordFieldIsEqual, isPasswordStrong } from '../util/ValidationUtil';
import { PasswordFormFields } from '../shared/PasswordFormFields';

const NOTICE_MESSAGES = ['error.general', 'change_password.invalid_old_password', 'form.error'];
const OLD_PW_FIELD_ID = 'oldPassword';

const ChangePasswordModal = observer((): React.JSX.Element => {
  const {
    passwordChangeStore,
    translateStore: { translate },
    uiStore: { notices, hideNotices, showErrorNoticeWithErroredFields },
  } = useStores();
  const { emailLoginPasswords, softAuthChange, isChangePasswordModalOpen, handleChangePasswordModalOpen } = passwordChangeStore;
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);

  useDidUpdate(() => {
    if (isChangePasswordModalOpen) {
      passwordChangeStore.init();
      hideNotices();
      setHasSubmitted(false);
    }
  }, [isChangePasswordModalOpen]);

  const getOldPasswordHelper = (forceIsDirty?: boolean) =>
    notEmptyFieldHelper(translate, emailLoginPasswords.oldPassword, forceIsDirty ?? hasSubmitted, OLD_PW_FIELD_ID);
  const getPasswordHelper = (forceIsDirty?: boolean) =>
    strongPwFieldHelper(translate, emailLoginPasswords.password, emailLoginPasswords.oldPassword || null, forceIsDirty ?? hasSubmitted);
  const getConfirmPwHelper = (forceIsDirty?: boolean) =>
    confirmPwFieldHelper(translate, emailLoginPasswords.password, emailLoginPasswords.confirmPassword, forceIsDirty ?? hasSubmitted);

  const renderNotice = () => {
    return notices?.map(
      (notice, index) =>
        ((notice.message && NOTICE_MESSAGES.includes(notice.message)) || NOTICE_MESSAGES.includes(`${notice.children}`)) && (
          <GridColumn key={`password-notice${index}`}>
            <Notice type={notice.type} attributes={{ 'aria-live': 'polite', 'aria-atomic': 'true' }}>
              <div>
                {translate(notice.message || `${notice.children}`)}
                {notice?.erroredFieldIds && notice.erroredFieldIds.length > 0 && (
                  <>
                    <Separator spacing="xxsmall" color="invisible" />
                    <List type="unstyled">
                      {notice.erroredFieldIds.map((erroredField) => (
                        <ListItem key={`erroredField-${erroredField}-button`}>
                          <Button
                            text={translate(`errored.field.button.${erroredField}`)}
                            type="withdrawal"
                            link
                            onClick={() => document.getElementById(erroredField)?.focus()}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </div>
            </Notice>
          </GridColumn>
        ),
    );
  };

  const renderOldPassword = (): React.JSX.Element | null => {
    if (!softAuthChange) {
      return null;
    }

    return (
      <>
        <Separator spacing="xxsmall" color="invisible" />
        <PassField
          label={translate('password.old_password_label')}
          id={OLD_PW_FIELD_ID}
          value={emailLoginPasswords.oldPassword}
          onChange={(value) => (emailLoginPasswords.oldPassword = value)}
          isInvalid={hasSubmitted && !emailLoginPasswords.oldPassword}
          errorMessage={getHelperErrorMessage(getOldPasswordHelper())}
        />
      </>
    );
  };

  const renderPasswordRules = () => {
    return (
      <Grid gutter="small">
        {Array.from({ length: 4 }, (i, idx) => `password.rules.${idx}`).map((text, idx) => (
          <GridColumn key={`password-rule-${idx}`}>
            <Grid noWrap={true} gutter="small">
              <GridColumn width={['min']}>
                <Icon modifiers="#info-round" size="xs" style={{ color: '#0094FF' }} />
              </GridColumn>
              <GridColumn width={['max']}>{translate(text)}</GridColumn>
            </Grid>
          </GridColumn>
        ))}
      </Grid>
    );
  };

  const isInvalid = () => {
    return (
      (softAuthChange && !emailLoginPasswords.oldPassword) ||
      (softAuthChange && emailLoginPasswords.oldPassword === emailLoginPasswords.password) ||
      !isPasswordStrong(emailLoginPasswords.password) ||
      !confirmPasswordFieldIsEqual(emailLoginPasswords)
    );
  };

  const clearState = () => {
    setHasSubmitted(false);
  };

  const handleFormSubmit = (event: React.FormEvent, callback?: () => void): void => {
    runInTeliaRumService('Parooli vahetamine', () => {
      event.preventDefault();
      setHasSubmitted(true);
      if (isInvalid()) {
        showErrorNoticeWithErroredFields('form.error', [
          softAuthChange ? getOldPasswordHelper(true) : undefined,
          getPasswordHelper(true),
          !!emailLoginPasswords.password && isPasswordStrong(emailLoginPasswords.password) ? getConfirmPwHelper(true) : undefined,
        ]);
        return;
      }
      passwordChangeStore.handleLogin(callback);
    });
  };

  return (
    <ModalProvider isClosable isOpen={isChangePasswordModalOpen} onToggle={(isOpen) => !isOpen && handleChangePasswordModalOpen(false)}>
      <Modal hasContentComponent={true} size="6-col">
        <ModalContent>
          <Text>
            <Grid align={['center-sm']}>
              <GridColumn width={['md-9']}>
                <Heading className="h3 text-center">{translate('changePasswordModal.header')}</Heading>
                <Separator spacing="xxsmall" color="invisible" />
              </GridColumn>
              {renderNotice()}
              <GridColumn width={['md-9']}>
                {renderPasswordRules()}
                {renderOldPassword()}
                <Separator spacing="xxsmall" color="invisible" />
                <PasswordFormFields
                  translate={translate}
                  emailLoginPasswords={emailLoginPasswords}
                  hasSubmitted={hasSubmitted}
                  passwordHelper={getPasswordHelper()}
                  confirmPasswordHelper={getConfirmPwHelper()}
                />
                <Separator spacing="xsmall" color="invisible" />
                <div className="text-center">
                  <Button
                    text={translate('password.change.btn')}
                    iconRight="#arrow-right"
                    onClick={(event) => {
                      handleFormSubmit(event, () => {
                        handleChangePasswordModalOpen(false);
                        clearState();
                      });
                    }}
                  />
                </div>
              </GridColumn>
            </Grid>
          </Text>
        </ModalContent>
      </Modal>
    </ModalProvider>
  );
});

export default ChangePasswordModal;
